 import React, { useState, useRef, useEffect, forwardRef } from 'react';
 import RequesterForm from './PostpaidRequesterForm';
 import {Table,TableBody,TableCell,TableContainer,TableRow,Paper,Button,Box
 } from '@mui/material';
 import { initialFormData } from './PostpaidFormDependency/PostpaidModel';
 import FormAutoFiller from '../FormAutoFiller';
 import { validatePostpaidForm, isBlankSimComplete, isMsisdnDataFilled, isVobbDataFilled, isEsimDataFilled } from './PostpaidFormDependency/PostpaidValidation';
 import { handleInputChange as handleInputChangeState } from './PostpaidFormDependency/PostpaidFormDetailsState';
 import { TestDataSection } from './PostpaidFormDependency/PostpaidTestDataSection';
 import { getMaterialCodeSuggestions, getStoreIdSuggestions } from './PostpaidFormDependency/PostpaidStoreIDMaterialCode';
 import GeneralForm from '../GeneralForm';
 import TotalQuantityDialog from '../TotalQuantityDialog';
 
const PostpaidForm = forwardRef(({ onRef, existingWebSocket, wsConnectionId, initialData, onFormDataChange, requesterInitialData: requesterInitialDataProp }, ref) => {
const [formData, setFormData] = useState(() => {
     if (initialData) {
       // Ensure general.environment is always present
       return {
         ...initialFormData,
         ...initialData,
         general: {
           ...initialFormData.general,
           ...initialData.general,
           environment: (initialData.general && initialData.general.environment) || initialFormData.general.environment || ''
         }
       };
     }
     return {
       ...initialFormData,
       general: {
         ...initialFormData.general,
         cr_project_name: initialFormData.general.cr_project_name || '',
         validity_period: initialFormData.general.validity_period || '',
         environment: initialFormData.general.environment || ''
       }
     };
   });
   // Add isBlankSimActive state
   const [isBlankSimActive, setIsBlankSimActive] = useState(false);
const [isMsisdnActive, setIsMsisdnActive] = useState(false);
const [isVobbActive, setIsVobbActive] = useState(false);
const [isEsimActive, setIsEsimActive] = useState(false);
   // Update parent component when form data changes
   useEffect(() => {
     if (onFormDataChange) {
       onFormDataChange(formData);
     }
   }, [formData, onFormDataChange]);
 
const [showFullForm, setShowFullForm] = useState(true);
const [showRequesterForm, setShowRequesterForm] = useState(false);
const [showTotalQuantityDialog, setShowTotalQuantityDialog] = useState(false);
const formRef = useRef(null);
const [formFiller, setFormFiller] = useState(null);
//setRequesterInitialData for sending to @common/RequesterForm.js
const [requesterInitialData, setRequesterInitialData] = useState(requesterInitialDataProp || null);
const [requesterResetTrigger, setRequesterResetTrigger] = useState(null);
const [errors, setErrors] = useState({
     general: {
       cr_project_name: '',
       validity_period: '',
       environment: ''
     }
   });
 
   const [userEmail, setUserEmail] = useState(null);
 
   const [formId] = useState(() => `Form_ID_${Math.random().toString(36).substring(2, 9
   )}`);

  const handleSubmit = (updatedFormData) => {
     // No longer sending data to attachForm.py via getFormDetails
     // Form data will be sent later in the requester form with all data combined
     console.log('Postpaid form data prepared for submission:', updatedFormData);
   };

   const calculateTotalQuantity = (formData) => {
     let total = 0;
     const postpaidData = formData.Postpaid || {};

     ['MSISDN', 'Blank Sim', 'eSIM', 'VOBB'].forEach(itemType => {
       const item = postpaidData[itemType];
       if (item && item.quantity) {
         const quantity = String(item.quantity).trim();
         if (quantity !== '') {
           const num = Number(quantity);
           if (!Number.isNaN(num) && Number.isInteger(num) && num > 0) {
             total += num;
           }
         }
       }
     });

     return total;
   };

   const proceedToNextStep = () => {
     const updatedFormData = { ...formData };
     handleSubmit(updatedFormData);
     
     const hasFilledData = (itemType) => {
       if (!updatedFormData.Postpaid || !updatedFormData.Postpaid[itemType]) return false;
       const item = updatedFormData.Postpaid[itemType];
       let storeId = '';
       if (item.store_id != null) {
         storeId = String(item.store_id).trim();
       } else if (item.field_store_id != null) {
         storeId = String(item.field_store_id).trim();
       }
       
       let quantity = '';
       if (item.quantity != null) {
         quantity = String(item.quantity).trim();
       } else if (item.field_quantity != null) {
         quantity = String(item.field_quantity).trim();
       }
       
       if (itemType === 'VOBB') {
         const region = item.region != null ? String(item.region).trim() : '';
         const exchangeArea = item.exchange_area != null ? String(item.exchange_area).trim() : '';
         const chargeArea = item.charge_area != null ? String(item.charge_area).trim() : '';
         const quantity = item.quantity != null ? String(item.quantity).trim() : '';
         return region !== '' && exchangeArea !== '' && chargeArea !== '' && quantity !== '';
       }

       return storeId !== '' && quantity !== '';
     };

     setRequesterInitialData({
       environment: updatedFormData.general.environment,
       projectName: updatedFormData.general.cr_project_name,
       ...updatedFormData,
       msisdnTest: hasFilledData('MSISDN'),
       vobbTest: hasFilledData('VOBB'),
       blanksimTest: hasFilledData('Blank Sim'),
       esimTest: hasFilledData('eSIM'),
       blanksim: hasFilledData('Blank Sim'),
       msisdn: hasFilledData('MSISDN'),
       vobb: hasFilledData('VOBB')
     });
     setShowRequesterForm(true);
     setShowFullForm(false);
   };

   const scrollToFirstError = () => {
     const errorElements = document.querySelectorAll('[data-error="true"]');
     if (errorElements.length > 0) {
       errorElements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
     }
   };
   
   const validateForm = () => {
     const { isValid, errors: newErrors } = validatePostpaidForm(formData);
     setErrors(newErrors);
     if (!isValid) {
       setTimeout(() => scrollToFirstError(), 100);
     }
     return isValid;
   };

   const handleShowFullForm = () => {
     setShowFullForm(true);
     setShowRequesterForm(false); // Hide requester form when showing full form
   };
 
   const handleInputChange = handleInputChangeState(formData, setFormData, setIsBlankSimActive);
 
   useEffect(() => {
     if (existingWebSocket) {
       const filler = new FormAutoFiller(existingWebSocket);
       setFormFiller(filler);
 
       // Handle messages using the existing connection
       const handleMessage = (event) => {
         console.log('[PostpaidForm] handleMessage called');
         try {
           const data = JSON.parse(event.data);
           console.log('[PostpaidForm] Received message type:', data.type);
           if (data.email) {
             setUserEmail(data.email);
           }
           // Listen for bedrockSRTicket and populate the main form data
           if (data.type === 'bedrockSRTicket' && data.formData) {
             console.log('[PostpaidForm] Processing bedrockSRTicket');
             console.log('[PostpaidForm] Raw data.formData:', data.formData);
             // Extract postpaid data and requester details from the formData structure
             const postpaidData = data.formData.postpaidData || {};
             const requesterDetails = data.formData.RequesterDetails || {};
             console.log('[PostpaidForm] Extracted RequesterDetails:', requesterDetails);
             const testbedOptions = data.formData.testbedOptions || {};
             const productionOptions = data.formData.productionOptions || {};

             // Hardcoded mapping for requester data - explicit field names only
             const mappedRequesterData = {
               requesterName: requesterDetails.requesterName || '',
               requesterEmail: requesterDetails.requesterEmail || '',
               requesterContact: requesterDetails.requesterContact || '',
               lineOwnerName: requesterDetails.lineOwnerName || '',
               lineOwnerEmail: requesterDetails.lineOwnerEmail || '',
               division: requesterDetails.division || '',
               environment: requesterDetails.environment ? requesterDetails.environment.toLowerCase() : '',
               department: requesterDetails.department || '',
               hodName: requesterDetails.hodName || '',
               projectName: requesterDetails.projectName || '',
               purpose: requesterDetails.purpose || '',
               location: requesterDetails.location || '',
               testingDate: requesterDetails.testingDate || new Date().toISOString().split('T')[0],
               // Hardcoded checkbox states from options
               vobbTest: testbedOptions.vobb ?? false,
               msisdnTest: testbedOptions.msisdn ?? false,
               esimTest: testbedOptions.esim ?? false,
               blanksimTest: testbedOptions.blanksim ?? false,
               blanksim: productionOptions.blanksim ?? false,
               msisdn: productionOptions.msisdn ?? false,
               vobb: productionOptions.vobb ?? false,
               formId: formId
             };
             
             // Update the main form data with the received data while preserving complete structure
             setFormData(prevFormData => {
               // Start with a deep copy of the complete initial form structure
               const updatedFormData = JSON.parse(JSON.stringify(initialFormData));
               
               // Then overlay with current form data to preserve any existing changes
               Object.keys(prevFormData).forEach(sectionKey => {
                 if (typeof prevFormData[sectionKey] === 'object' && prevFormData[sectionKey] !== null) {
                   if (sectionKey === 'general') {
                     updatedFormData[sectionKey] = {
                       ...updatedFormData[sectionKey],
                       ...prevFormData[sectionKey]
                     };
                   } else {
                     // For other sections, merge each item carefully
                     Object.keys(prevFormData[sectionKey]).forEach(itemKey => {
                       // Ensure the section exists in updatedFormData
                       if (!updatedFormData[sectionKey]) {
                         updatedFormData[sectionKey] = {};
                       }

                       // Check if prevFormData item is a valid object before spreading
                       if (typeof prevFormData[sectionKey][itemKey] === 'object' && prevFormData[sectionKey][itemKey] !== null) {
                         // Initialize the item if it doesn't exist
                         if (!updatedFormData[sectionKey][itemKey]) {
                           updatedFormData[sectionKey][itemKey] = {};
                         }
                           updatedFormData[sectionKey][itemKey] = {
                             ...updatedFormData[sectionKey][itemKey],
                             ...prevFormData[sectionKey][itemKey]
                           };
                         }
                     });
                   }
                 }
               });
               
               // HARDCODED MAPPING: Update with received data from bedrockSRTicket
               // Always use explicit field names - never dynamic iteration

               // Update general fields - hardcoded field names
               if (postpaidData.general) {
                 updatedFormData.general = {
                   ...updatedFormData.general,
                   cr_project_name: postpaidData.general.cr_project_name || updatedFormData.general.cr_project_name,
                   validity_period: postpaidData.general.validity_period || updatedFormData.general.validity_period,
                   environment: postpaidData.general.environment ? postpaidData.general.environment.toLowerCase() : updatedFormData.general.environment
                 };
               }

               // NORMALIZE AND SORT POSTPAID FIELDS: Ensure consistent field order
               // Define the correct field order that should always be used
               const fieldOrder = ['store_id', 'material_code', 'quantity'];

               // Helper function to create normalized field object in correct order
               // Also convert all values to strings to handle integer values from backend
               const vobbFieldOrder = ['region', 'exchange_area', 'charge_area', 'quantity'];
               const normalizeFields = (sourceData, itemKey) => {
                 const normalized = {};
                 const order = itemKey === 'VOBB' ? vobbFieldOrder : fieldOrder;
                 order.forEach(field => {
                   const value = sourceData?.[field];
                   // Convert to string if value exists, otherwise empty string
                   normalized[field] = (value != null && value !== '') ? String(value) : '';
                 });
                 return normalized;
               };

               // NORMALIZE Postpaid data to ensure consistent field order
               // Only normalize items that exist in Bedrock data
               if (updatedFormData.Postpaid && postpaidData.Postpaid) {
                 const items = ['MSISDN', 'Blank Sim', 'eSIM', 'VOBB'];
                 items.forEach(itemKey => {
                   if (postpaidData.Postpaid[itemKey]) {
                     // Merge Bedrock data with existing data, then normalize
                     updatedFormData.Postpaid[itemKey] = normalizeFields({
                       ...updatedFormData.Postpaid[itemKey],
                       ...postpaidData.Postpaid[itemKey]
                     }, itemKey);
                   }
                 });
               }
               
               console.log('[PostpaidForm] Updated form data with complete structure:', updatedFormData);
               return updatedFormData;
             });
             setRequesterInitialData(mappedRequesterData);
             setShowRequesterForm(true);
             setShowFullForm(true);
           }
         } catch (error) {
           console.error('Error parsing WebSocket message:', error);
         }
       };
 
       existingWebSocket.addEventListener('message', handleMessage);
 
       return () => {
         existingWebSocket.removeEventListener('message', handleMessage);
       };
     }
   }, [existingWebSocket, wsConnectionId, formId]);
   
     // Expose methods to parent component
     useEffect(() => {
       if (onRef) {
         onRef({
           showForm: () => setShowFullForm(true),
         });
       }
     }, [onRef]);
   
     // Helper functions to check if test data fields are active
    const checkBlankSimActive = (data) => {
       const blankSimData = data?.Postpaid?.['Blank Sim'] || {};
       return isBlankSimComplete(blankSimData);
     };
 
     // Monitor all test data fields in a single useEffect to avoid redundant executions
     useEffect(() => {
       const postpaidData = formData?.Postpaid || {};
       
       // Check Blank Sim
       const isBlankSimFilled = checkBlankSimActive(formData);
       setIsBlankSimActive(isBlankSimFilled);
       
       // Check MSISDN
       const msisdnData = postpaidData['MSISDN'] || {};
       const isMsisdnFilled = isMsisdnDataFilled(msisdnData);
       setIsMsisdnActive(isMsisdnFilled);
       
       // Check VOBB
       const vobbData = postpaidData['VOBB'] || {};
       const isVobbFilled = isVobbDataFilled(vobbData);
       setIsVobbActive(isVobbFilled);
       
       // Check eSIM
       const esimData = postpaidData['eSIM'] || {};
       const isEsimFilled = isEsimDataFilled(esimData);
       setIsEsimActive(isEsimFilled);

       // Logging
       if (isBlankSimFilled) {
         console.log("Blank Sim data is filled. Disabling other test data items.");
       } else {
         console.log("Blank Sim data is not filled. Re-enabling other test data items.");
       }
       
       if (isMsisdnFilled) {
         console.log("MSISDN data is filled. May disable Blank Sim.");
       }
       
       if (isVobbFilled) {
         console.log("VOBB data is filled. May disable Blank Sim.");
       }
       
       if (isEsimFilled) {
         console.log("eSIM data is filled. May disable Blank Sim.");
       }
     }, [formData?.Postpaid?.['Blank Sim'], formData?.Postpaid?.['MSISDN'], formData?.Postpaid?.['VOBB'], formData?.Postpaid?.['eSIM']]);
     
 
     const isFieldDisabled = (_, item) => {
       // Handle environment-specific restrictions
       if (item === 'eSIM' && formData.general.environment === 'production') {
         return true;
       }
      
       // If Blank Sim has any field filled, disable all other items
       if (isBlankSimActive && item !== 'Blank Sim') {
         return true;
       }
      
       // If any other field has data, disable Blank Sim
       if (item === 'Blank Sim' && (isMsisdnActive || isVobbActive || isEsimActive)) {
         return true;
       }
      
       return false;
     }; 
     // Reset the disabled state when "Blank Sim" is cleared
     useEffect(() => {
       if (!isBlankSimActive) {
         console.log("Blank Sim is cleared. Test data items are now enabled.");
       }
     }, [isBlankSimActive]);
 
     useEffect(() => {
           if (formData.general.environment === 'production') {
             console.log("Switching to production environment");
              setFormData(prevData => {
              const updatedData = { ...prevData };
              // Clear eSIM data in production
              if (updatedData.Postpaid && updatedData.Postpaid.eSIM) {
                updatedData.Postpaid.eSIM = {
                  store_id: "",
                  material_code: "",
                  quantity: ""
                };
              }
              return updatedData;
            });
 
           } 
         }, [formData.general]);
     
     const clearFormData = () => {
       setFormData(prevData => {
         const clearedData = { ...prevData };
         // Store Blank Sim data before clearing if it exists and is filled
         const blankSimData = clearedData.Postpaid?.["Blank Sim"];
         // Use OR logic to preserve any data in Blank Sim fields
         const isBlankSimFilled = !!((blankSimData?.store_id != null && String(blankSimData.store_id).trim()) ||
                                    (blankSimData?.material_code != null && String(blankSimData.material_code).trim()) ||
                                    (blankSimData?.quantity != null && String(blankSimData.quantity).trim()));
         
         // Clear all sections except general
         Object.entries(clearedData).forEach(([sectionKey, sectionItems]) => {
           if (sectionKey !== "general" && typeof sectionItems === 'object' && sectionItems !== null) {
             Object.keys(sectionItems).forEach(itemKey => {
               // Skip clearing Blank Sim data only if it's filled
               if (sectionKey === "Postpaid" && itemKey === "Blank Sim" && isBlankSimFilled) {
                 return;
               }
               clearedData[sectionKey][itemKey] = {
                 store_id: "",
                 material_code: "",
                 quantity: ""
               };
             });
           }
         });
         // Restore Blank Sim data only if it was filled
         if (isBlankSimFilled && clearedData.Postpaid) {
           clearedData.Postpaid["Blank Sim"] = blankSimData;
         }
         return clearedData;
       });
     };
     
   // Update requester form data when main form data changes
   useEffect(() => {
     if (showRequesterForm && requesterInitialData) {
       const hasFilledData = (itemType) => {
         if (!formData.Postpaid || !formData.Postpaid[itemType]) return false;
         const item = formData.Postpaid[itemType];
         // Get store_id, preferring store_id over field_store_id
         let storeId = '';
         if (item.store_id != null) {
           storeId = String(item.store_id).trim();
         } else if (item.field_store_id != null) {
           storeId = String(item.field_store_id).trim();
         }
         
         // Get quantity, preferring quantity over field_quantity
         let quantity = '';
         if (item.quantity != null) {
           quantity = String(item.quantity).trim();
         } else if (item.field_quantity != null) {
           quantity = String(item.field_quantity).trim();
         }
         // VOBB has different field structure
        if (itemType === 'VOBB') {
          const region = item.region != null ? String(item.region).trim() : '';
          const exchangeArea = item.exchange_area != null ? String(item.exchange_area).trim() : '';
          const chargeArea = item.charge_area != null ? String(item.charge_area).trim() : '';
          const quantity = item.quantity != null ? String(item.quantity).trim() : '';
          return region !== '' && exchangeArea !== '' && chargeArea !== '' && quantity !== '';
        }

        return storeId !== '' && quantity !== '';
       };

       setRequesterInitialData(prevData => ({
         ...prevData,
         environment: formData.general.environment,
         projectName: formData.general.cr_project_name,
         // Include the complete form data for PDF generation
         ...formData,
         // For testbed environment
         msisdnTest: hasFilledData('MSISDN'),
         vobbTest: hasFilledData('VOBB'),
         blanksimTest: hasFilledData('Blank Sim'),
         esimTest: hasFilledData('eSIM'),
         // For production environment
         blanksim: hasFilledData('Blank Sim'),
         msisdn: hasFilledData('MSISDN'),
         vobb: hasFilledData('VOBB')
       }));
     }
   }, [formData, showRequesterForm]);
     useEffect(() => {
       if (isBlankSimActive) {
         console.log("Blank Sim data is filled. Disabling other test data items.");
         // Clear form data inline to avoid dependency issues
         setFormData(prevData => {
           const clearedData = { ...prevData };
           // Store Blank Sim data before clearing if it exists and is filled
           const blankSimData = clearedData.Postpaid?.["Blank Sim"];
           // Use OR logic to preserve any data in Blank Sim fields
           const isBlankSimFilled = !!((blankSimData?.store_id != null && String(blankSimData.store_id).trim()) ||
                                      (blankSimData?.material_code != null && String(blankSimData.material_code).trim()) ||
                                      (blankSimData?.quantity != null && String(blankSimData.quantity).trim()));
           
           // Clear all sections except general
           Object.entries(clearedData).forEach(([sectionKey, sectionItems]) => {
             if (sectionKey !== "general" && typeof sectionItems === 'object' && sectionItems !== null) {
               Object.keys(sectionItems).forEach(itemKey => {
                 // Skip clearing Blank Sim data only if it's filled
                 if (sectionKey === "Postpaid" && itemKey === "Blank Sim" && isBlankSimFilled) {
                   return;
                 }
                 clearedData[sectionKey][itemKey] = {
                   store_id: "",
                   material_code: "",
                   quantity: ""
                 };
               });
             }
           });
           // Restore Blank Sim data only if it was filled
           if (isBlankSimFilled && clearedData.Postpaid) {
             clearedData.Postpaid["Blank Sim"] = blankSimData;
           }
           return clearedData;
         });
       } else {
         console.log("Blank Sim data is not filled. Re-enabling other test data items.");
       }
     }, [isBlankSimActive]);

   return (
         <Box
             component="form"
             noValidate
             ref={formRef}
             onSubmit={(event) => {
               event.preventDefault();
               const isValid = validateForm();
               if (!isValid) {
                 return;
               }
               
               // Calculate total quantity
               const totalQuantity = calculateTotalQuantity(formData);
               
               // Only show dialog if quantity exceeds 100
               if (totalQuantity > 100) {
                 setShowTotalQuantityDialog(true);
               } else {
                 // Proceed directly to next step
                 proceedToNextStep();
               }
             }}
           >
         {!showFullForm && (
           <Box sx={{ textAlign: 'center', mt: 4 }}>
             <Button variant="contained" color="primary" size="large" onClick={handleShowFullForm}>
               Click to open previous form filled up
             </Button>
           </Box>
         )}
   
         {showFullForm && (
             <Box sx={{ display: 'flex', gap: 2 }}>
               <Box sx={{ flex: 1 }}>
               <TableContainer component={Paper}>
                 <Table>
                   <TableBody>
                     {/* Header Information */}
                     <GeneralForm 
                       formData={formData}
                       errors={errors}
                       handleInputChange={handleInputChange}
                       environment={formData.general.environment}
                     />
 
                     {/* Store ID, Material Code, Quantity section */}
                     <TableRow>
                       <TableCell sx={{ fontWeight: 'bold', color:'Red' }} colSpan={4}>
                         Note: fields that are highlighted in grey cannot be filled up 
                         {formData.general.environment === 'production' ? 
                           " (Blank Sim has to be requested separately, and the item eSIM is not available in production environment)" :
                           " (Blank Sim has to be requested separately)"}
                       </TableCell>
                     </TableRow>
 
                     {/* Render form based on PostpaidModel.js structure (single source of truth) */}
                     <React.Fragment key="Postpaid">
                       <TableRow>
                         <TableCell sx={{ fontWeight: 'bold' }} colSpan={4}>Postpaid</TableCell>
                       </TableRow>

                       {/* Loop through items defined in PostpaidModel.js only */}
                       {Object.keys(initialFormData.Postpaid).map((item) => {
                         const fields = formData.Postpaid[item] || initialFormData.Postpaid[item];

                         // Special rendering for VOBB - 2x2 grid layout
                         if (item === 'VOBB') {
                           return (
                             <React.Fragment key={`Postpaid-${item}`}>
                               {/* First row: Item name + Postal Code + State */}
                               <TableRow
                                 data-error={!!errors.Postpaid?.[item]?.postal_code || !!errors.Postpaid?.[item]?.state}
                                 sx={{
                                   backgroundColor: (errors.Postpaid?.[item]?.postal_code || errors.Postpaid?.[item]?.state) ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                                 }}
                               >
                                 <TableCell rowSpan={2}>{item}</TableCell>
                                 <TableCell>
                                   <TestDataSection
                                     section="Postpaid"
                                     item={item}
                                     fields={fields}
                                     fieldName="region"
                                     fieldValue={fields.region}
                                     isFieldDisabled={isFieldDisabled}
                                     errors={errors}
                                     handleInputChange={handleInputChange}
                                     getMaterialCodeSuggestions={getMaterialCodeSuggestions}
                                     getStoreIdSuggestions={getStoreIdSuggestions}
                                     formData={formData}
                                   />
                                 </TableCell>
                                 <TableCell>
                                   <TestDataSection
                                     section="Postpaid"
                                     item={item}
                                     fields={fields}
                                     fieldName="exchange_area"
                                     fieldValue={fields.exchange_area}
                                     isFieldDisabled={isFieldDisabled}
                                     errors={errors}
                                     handleInputChange={handleInputChange}
                                     getMaterialCodeSuggestions={getMaterialCodeSuggestions}
                                     getStoreIdSuggestions={getStoreIdSuggestions}
                                     formData={formData}
                                   />
                                 </TableCell>
                               </TableRow>
                               {/* Second row: Charge Area + Quantity */}
                               <TableRow
                                 data-error={!!errors.Postpaid?.[item]?.charge_area || !!errors.Postpaid?.[item]?.quantity}
                                 sx={{
                                   backgroundColor: (errors.Postpaid?.[item]?.charge_area || errors.Postpaid?.[item]?.quantity) ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                                 }}
                               >
                                 <TableCell>
                                   <TestDataSection
                                     section="Postpaid"
                                     item={item}
                                     fields={fields}
                                     fieldName="charge_area"
                                     fieldValue={fields.charge_area}
                                     isFieldDisabled={isFieldDisabled}
                                     errors={errors}
                                     handleInputChange={handleInputChange}
                                     getMaterialCodeSuggestions={getMaterialCodeSuggestions}
                                     getStoreIdSuggestions={getStoreIdSuggestions}
                                     formData={formData}
                                   />
                                 </TableCell>
                                 <TableCell>
                                   <TestDataSection
                                     section="Postpaid"
                                     item={item}
                                     fields={fields}
                                     fieldName="quantity"
                                     fieldValue={fields.quantity}
                                     isFieldDisabled={isFieldDisabled}
                                     errors={errors}
                                     handleInputChange={handleInputChange}
                                     getMaterialCodeSuggestions={getMaterialCodeSuggestions}
                                     getStoreIdSuggestions={getStoreIdSuggestions}
                                     formData={formData}
                                   />
                                 </TableCell>
                               </TableRow>
                             </React.Fragment>
                           );
                         }

                         // Default rendering for other items (MSISDN, Blank Sim, eSIM)
                         return (
                           <React.Fragment key={`Postpaid-${item}`}>
                             <TableRow
                               data-error={!!errors.Postpaid?.[item]?.quantity || !!errors.Postpaid?.[item]?.material_code}
                               sx={{
                                 backgroundColor: (errors.Postpaid?.[item]?.quantity || errors.Postpaid?.[item]?.material_code) ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                               }}
                             >
                               <TableCell>{item}</TableCell>
                               {Object.keys(fields).map((fieldName) => (
                                 <TableCell
                                   key={`Postpaid-${item}-${fieldName}`}
                                   sx={{
                                     ...(fieldName === 'store_id' && { width: '200px' }),
                                     ...(fieldName === 'material_code' && { width: '200px' })
                                   }}
                                 >
                                   <TestDataSection
                                     section="Postpaid"
                                     item={item}
                                     fields={fields}
                                     fieldName={fieldName}
                                     fieldValue={fields[fieldName]}
                                     isFieldDisabled={isFieldDisabled}
                                     errors={errors}
                                     handleInputChange={handleInputChange}
                                     getMaterialCodeSuggestions={getMaterialCodeSuggestions}
                                     getStoreIdSuggestions={getStoreIdSuggestions}
                                     formData={formData}
                                   />
                                 </TableCell>
                               ))}
                             </TableRow>
                           </React.Fragment>
                         );
                       })}
                     </React.Fragment>
                 </TableBody>
               </Table>
             </TableContainer>
             </Box>
           </Box>
       )}
 
       {showFullForm && (
         <Box sx={{ mt: 4, textAlign: 'center' }}>
           <Button type="submit" variant="contained" color="primary" size="large">
             NEXT
           </Button>
         </Box>
       )}

       {/* Total Quantity Dialog - modal popup shown only when quantity exceeds 100 */}
       <TotalQuantityDialog
         open={showTotalQuantityDialog}
         onClose={() => setShowTotalQuantityDialog(false)}
         formData={formData}
         category="postpaid"
         maxQuantity={100}
       />

       <Box sx={{ display: showRequesterForm ? 'block' : 'none' }}>
         <RequesterForm
           initialData={requesterInitialData}
           requesterData={requesterInitialDataProp}
           ws={formFiller?.ws}
           resetTrigger={requesterResetTrigger}
           onSubmit={({ status, resetPreviousForm }) => {
             if (status === 'success' && resetPreviousForm) {
               // Reset all form fields to their initial state
               const freshInitialState = {
                 ...JSON.parse(JSON.stringify(initialFormData)),
                 general: {
                   cr_project_name: '',
                   environment: '',
                   validity_period: '',
                 },
                 Postpaid: Object.keys(initialFormData.Postpaid).reduce((acc, key) => ({
                   ...acc,
                   [key]: {
                     store_id: '',
                     material_code: '',
                     quantity: ''
                   }
                 }), {})
               };

               setFormData(freshInitialState);

               if (onFormDataChange) {
                 onFormDataChange(freshInitialState);
               }

               setIsBlankSimActive(false);
               setErrors({
                 general: {
                   cr_project_name: '',
                   validity_period: '',
                   environment: '',
                   total_quantity: ''
                 }
               });

               // Reset the RequesterForm state by changing the resetTrigger
               setRequesterResetTrigger(Date.now());

               // Keep the requester form showing
               // This maintains the requester form with its data
               // while clearing the main form data
             }
           }}
         />
       </Box>
     </Box>
   );
 });

 export default PostpaidForm;